import { useEffect, useRef, type ReactNode } from "react";

import { Trans } from "@lingui/react/macro";
import { Loader2 } from "lucide-react";

import { Button } from "./button";
import { ResponsiveSheet } from "./responsive-sheet";

interface ConfirmSheetProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Label of the confirm button, e.g. <Trans>Delete</Trans>. */
  confirmLabel: ReactNode;
  /** Defaults to "Cancel". */
  cancelLabel?: ReactNode;
  /** `destructive` for deletes/removals; `default` for a plain confirm. */
  tone?: "destructive" | "default";
  /** Disables both buttons and spins the confirm button. */
  pending?: boolean;
  /** Icon shown before the confirm label (e.g. <Trash2 />). */
  icon?: ReactNode;
  /** Screen-reader name of the dialog (falls back to the title). */
  ariaLabel?: string;
  closeLabel?: string;
  /** Optional body under the description (extra context, a checkbox…). */
  children?: ReactNode;
  /** Set when the confirm opens on top of another sheet: closing a stacked
   *  sheet through the history sentinel would also close the one below. */
  disableHistory?: boolean;
}

/**
 * Small confirm dialog on <ResponsiveSheet> — bottom sheet on mobile,
 * dialog on desktop. Replaces native `confirm()` (P20); same layout as the
 * family "Remove member" confirm.
 */
export function ConfirmSheet({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = "destructive",
  pending = false,
  icon,
  ariaLabel,
  closeLabel,
  children,
  disableHistory = false,
}: ConfirmSheetProps) {
  // One confirm per opening: the sheet keeps a tappable button while it
  // animates out, and a second tap would re-run a delete (native confirm()
  // blocked that). Re-armed on the next open, or when a `pending` caller
  // settles with the sheet still open (a failed request may be retried).
  const confirmedRef = useRef(false);
  useEffect(() => {
    if (open && !pending) confirmedRef.current = false;
  }, [open, pending]);
  const handleConfirm = () => {
    if (confirmedRef.current) return;
    confirmedRef.current = true;
    onConfirm();
  };

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      width="sm"
      ariaLabel={ariaLabel}
      closeLabel={closeLabel}
      disableHistory={disableHistory}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" size="touch" onClick={onClose} disabled={pending}>
            {cancelLabel ?? <Trans>Cancel</Trans>}
          </Button>
          <Button
            variant={tone === "destructive" ? "destructive" : "default"}
            size="touch"
            onClick={handleConfirm}
            disabled={pending}
          >
            {pending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
            {confirmLabel}
          </Button>
        </div>
      }
    >
      {children ?? <div aria-hidden className="h-1" />}
    </ResponsiveSheet>
  );
}
